import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const API_BASE: string = (import.meta.env.VITE_PUBLIC_API_URL as string) || "/api";

interface SiteSettingsValue {
  /** Raw public settings map (key -> value). */
  settings: Record<string, string>;
  /** Whether the first fetch has finished. */
  loading: boolean;
  /** Convenience getter with a fallback. */
  get: (key: string, fallback?: string) => string;
}

const DEFAULT_VALUE: SiteSettingsValue = {
  settings: {},
  loading: true,
  get: (_key, fallback = "") => fallback,
};

const SiteSettingsContext = createContext<SiteSettingsValue>(DEFAULT_VALUE);

/** Point the browser tab favicon at the uploaded brand favicon (if set). */
function applyFavicon(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/svg+xml";
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Loads public site settings (site name, logo, favicon, SEO defaults, contact
 * info) once on mount, caches them in context, and applies the favicon.
 * Wrap <App/> with <SiteSettingsProvider> and read via useSiteSettings().
 */
export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const load = () => {
      fetch(`${API_BASE}/public/settings`)
        .then((res) => res.json().catch(() => null))
        .then((body: any) => {
          if (!active) return;
          const map: Record<string, string> =
            body && typeof body === "object" && body.data ? body.data : {};
          setSettings(map);
          if (map.brand_favicon_url) applyFavicon(map.brand_favicon_url);
        })
        .catch(() => {
          /* Public settings are a progressive enhancement — fail silently. */
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    };

    load();
    // Re-fetch after the admin saves branding/SEO so changes appear immediately.
    window.addEventListener("site-settings-updated", load);
    return () => {
      active = false;
      window.removeEventListener("site-settings-updated", load);
    };
  }, []);

  const value = useMemo<SiteSettingsValue>(
    () => ({
      settings,
      loading,
      get: (key, fallback = "") => settings[key] ?? fallback,
    }),
    [settings, loading]
  );

  return (
    <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}